import { useCallback, useSyncExternalStore } from 'react';
import { defineStoredJson } from '../../utils/storage';

/**
 * How a field list is ordered, chosen by whoever holds the device and kept for
 * the next time. Local rather than session storage, unlike the search: an
 * order hides nothing, so carrying it over to the next launch is no trap.
 */

export type SortDirection = 'asc' | 'desc';

/** One way a field list can be ordered. */
export interface FieldSortOption {
    /** The row value it sorts by — a key of the rows' `sortValues`. */
    key: string;
    labelId: string;
    /**
     * The direction it starts in when picked: a date usually newest first.
     * Ascending where omitted.
     */
    direction?: SortDirection;
}

export interface FieldListSort {
    key: string;
    direction: SortDirection;
}

type Store = Record<string, FieldListSort>;

const isDirection = (value: unknown): value is SortDirection =>
    value === 'asc' || value === 'desc';

const listSorts = defineStoredJson<Store>({
    key: 'field_list_sort',
    area: 'local',
    fallback: () => ({}),
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Store)
            : undefined
});

/** The order an option starts in. */
export function sortOf(option: FieldSortOption): FieldListSort {
    return { key: option.key, direction: option.direction ?? 'asc' };
}

/**
 * The order a list stands in: the stored one while it still names one of the
 * options, else the first option's.
 */
export function storedSort(
    listId: string,
    options: readonly FieldSortOption[]
): FieldListSort {
    const stored = listSorts.read()[listId];
    if (
        stored &&
        isDirection(stored.direction) &&
        options.some((option) => option.key === stored.key)
    ) {
        return { key: stored.key, direction: stored.direction };
    }
    return sortOf(options[0]);
}

export function saveSort(listId: string, sort: FieldListSort): void {
    // Storage refused — the order simply holds until the list is left.
    listSorts.write({ ...listSorts.read(), [listId]: sort });
}

/** The order the list is in, and how to change it — remembered per list. */
export function useFieldListSort(
    listId: string,
    options: readonly FieldSortOption[]
): [FieldListSort, (sort: FieldListSort) => void] {
    const key = useSyncExternalStore(
        (onChange) => listSorts.subscribe(onChange),
        () => {
            const sort = storedSort(listId, options);
            return `${sort.key}:${sort.direction}`;
        }
    );
    const [sortKey, direction] = key.split(':');
    const setSort = useCallback(
        (sort: FieldListSort) => saveSort(listId, sort),
        [listId]
    );
    return [{ key: sortKey, direction: direction as SortDirection }, setSort];
}
